import { type PropertyValues } from 'lit';
import type { ChromelessPlayer } from 'theoplayer/chromeless';
import { stateReceiver } from './StateReceiverMixin';
import { CastButton } from './CastButton';
import chromecastButtonHtml from './ChromecastButton.html';
import chromecastButtonCss from './ChromecastButton.css';
import { customElement, property } from 'lit/decorators.js';
import { getLocale } from '../i18n';
import { Attribute } from '../util/Attribute';

let chromecastButtonId = 0;

/**
 * A button to start and stop casting using Chromecast.
 */
@customElement('theoplayer-chromecast-button')
@stateReceiver(['player', 'lang'])
export class ChromecastButton extends CastButton {
    static styles = [...CastButton.styles, chromecastButtonCss];

    private _player: ChromelessPlayer | undefined;
    private readonly _buttonId = ++chromecastButtonId;

    constructor() {
        super();
    }

    override connectedCallback() {
        super.connectedCallback();
        this._updateAriaLabel();
    }

    get player() {
        return this._player;
    }

    set player(player: ChromelessPlayer | undefined) {
        this._player = player;
        this.castApi = player?.cast?.chromecast;
    }

    @property({ reflect: true, type: String, attribute: Attribute.LANG })
    accessor lang: string = '';

    override willUpdate(changedProperties: PropertyValues) {
        super.willUpdate(changedProperties);
        this._updateAriaLabel();
    }

    private _updateAriaLabel(): void {
        const locale = getLocale(this.lang);
        this.ariaLabel = this.castState === 'connecting' || this.castState === 'connected' ? locale.chromecastConnectedAria : locale.chromecastAria;
    }

    protected override render() {
        return chromecastButtonHtml(this._buttonId);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'theoplayer-chromecast-button': ChromecastButton;
    }
}
